import { useEffect, useRef, useState } from 'react';
import { Archive, MessageSquare, RefreshCw, Save } from 'lucide-react';
import { platformApi, type BacklogItem, type Bug, type FeatureRequest, type FeedbackComment, type FeedbackKind } from '../../services/platformApi';
import { formatDateTime } from '../../shared/dateTimeFormatting';

export function FeedbackCurationPanel({ kind, item, disabled, onUpdated, onBusy, startPromotion = false }: { kind: FeedbackKind; item: Bug | FeatureRequest; disabled: boolean; onUpdated: (item: Bug | FeatureRequest, moved: boolean) => void; onBusy: (busy: boolean) => void; startPromotion?: boolean }) {
  const [comments, setComments] = useState<FeedbackComment[]>([]);
  const [backlog, setBacklog] = useState<BacklogItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [moving, setMoving] = useState(startPromotion);
  const promotionTitle = useRef<HTMLInputElement>(null);
  const [values, setValues] = useState({ title: item.title, description: item.description, priority: item.priority || 'medium' });
  const pending = useRef(false);
  useEffect(() => {
    let live = true; setLoading(true); setError('');
    platformApi.curation(kind, item.id).then(result => { if (live) { setComments(result.comments); setBacklog(result.backlog); } }).catch(reason => { if (live) setError(reason instanceof Error ? reason.message : String(reason)); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [kind, item.id, item.revision, retry]);
  useEffect(() => {
    if (!loading && moving && !backlog) {
      promotionTitle.current?.focus();
      promotionTitle.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [loading, moving, backlog]);
  const transact = async (move: boolean) => {
    if (pending.current || disabled) return;
    pending.current = true; onBusy(true); setError('');
    try {
      if (move) {
        const result = await platformApi.moveToBacklog(kind, item.id, item.revision, values);
        setBacklog(result.backlog); setMoving(false); onUpdated(result.item, true);
      } else {
        const result = await platformApi.comment(kind, item.id, item.revision, body);
        setComments(current => [...current, result.comment]); setBody(''); onUpdated(result.item, false);
      }
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { pending.current = false; onBusy(false); }
  };
  const backlogLink = new URLSearchParams(window.location.search);
  backlogLink.set('platformAdminTab', 'backlog');
  backlogLink.delete('platformFeedbackId');
  if (backlog) backlogLink.set('platformBacklogId', backlog.id);
  return <section aria-label="Private feedback curation">
    <h3>Comments</h3><p>Current revision: {item.revision}</p>
    {loading ? <p role="status">Loading comments...</p> : <>
      {comments.map(comment => <article key={comment.id}><p>{comment.actorId} · {formatDateTime(comment.createdAt)}</p><p className="platform-prose">{comment.body}</p></article>)}
      {!comments.length && <p>No comments.</p>}
      <form onSubmit={event => { event.preventDefault(); void transact(false); }}>
        <fieldset className="platform-fields" disabled={disabled}><label>New private comment<textarea required maxLength={4000} value={body} onChange={event => setBody(event.target.value)} /></label></fieldset>
        <button disabled={disabled || !body.trim()}><MessageSquare size={16} />Add comment</button>
      </form>
      <h3>{kind === 'bug' ? 'Internal bug fix / backlog' : 'Internal backlog'}</h3>
      {backlog ? <p>Backlog: <a href={`?${backlogLink}`}>{backlog.title}</a> · {backlog.status} · Source retained: {item.reference}</p> : <button type="button" disabled={disabled} onClick={() => setMoving(value => !value)}><Archive size={16} />Move to backlog</button>}
      {moving && !backlog && <form aria-label="Move to backlog" onSubmit={event => { event.preventDefault(); void transact(true); }}>
        <fieldset className="platform-fields" disabled={disabled}>
          <label>Backlog title<input ref={promotionTitle} required maxLength={200} value={values.title} onChange={event => setValues(current => ({ ...current, title: event.target.value }))} /></label>
          <label>Backlog description<textarea required maxLength={8000} value={values.description} onChange={event => setValues(current => ({ ...current, description: event.target.value }))} /></label>
          <label>Backlog priority<select value={values.priority} onChange={event => setValues(current => ({ ...current, priority: event.target.value }))}>{['low', 'medium', 'high', 'blocking'].map(priority => <option key={priority}>{priority}</option>)}</select></label>
        </fieldset>
        <button disabled={disabled || !values.title.trim() || !values.description.trim()}><Save size={16} />Save to backlog</button><button type="button" disabled={disabled} onClick={() => setMoving(false)}>Cancel move</button>
      </form>}
    </>}
    {error && <p role="alert">{error}</p>}
    <button disabled={disabled || loading} onClick={() => setRetry(value => value + 1)}><RefreshCw size={16} />Refresh comments</button>
  </section>;
}